'use client'

import React from 'react'
import { useState, useEffect } from 'react'
import {
  Drawer,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Spin,
  Progress,
  Divider,
  Tag,
  message,
  Table,
} from 'antd'
import {
  EditOutlined,
  BookOutlined,
  BulbOutlined,
  FontSizeOutlined,
  CheckCircleOutlined,
  MailOutlined,
  SoundOutlined,
  CloseCircleFilled,
  AudioOutlined
} from '@ant-design/icons'
import { Descriptions } from 'antd'

import moment from 'moment'
import { useDispatch } from 'react-redux'
import { fetchAptitudeScore } from '../../actions'
import styled from 'styled-components'

const { Title, Text, Paragraph } = Typography

// Professional styled components
const DrawerContainer = styled.div`
  background: #fafafa;
  min-height: 100%;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
  margin: -16px -8px 24px -8px;
`

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #262626;
`

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
`
const FillerWord = styled.span`
  color: #d4380d;
  background: #fff2e8;
  border-radius: 3px;
  padding: 2px 4px;
  font-weight: 500;
  margin: 0 2px;
`
const ContentSection = styled.div`
  margin: 0 8px 24px 8px;
`

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0 0 16px 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 12px;
  }
`

// Aptitude specific styled components
const OverallScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }
`

const ScoreCard = styled.div`
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ScoreValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ScoreLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const ScoreSubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
`

const SummaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  transition: all 0.2s ease;
  height: 100%; /* Make all cards fill container height */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 100px; 
  max-width: 180px;
  
  &:hover {
    border-color: #d9d9d9;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const SummaryLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const DifficultyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const DifficultyCard = styled.div`
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  border: 2px solid ${props => props.borderColor};
  background: ${props => props.bgColor};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const DifficultyLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 8px;
  }
`

const DifficultyScore = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const DifficultyPercentage = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const ResponsiveTableContainer = styled.div`
  margin-bottom: 24px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  overflow: hidden;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    border-bottom: 2px solid #e8e8e8;
    font-weight: 600;
    color: #262626;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 8px;
  }

  .ant-table-tbody > tr > td {
    padding: 12px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #595959;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f9fafb;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafafa;
  }

  .ant-table-tbody > tr:nth-child(even):hover > td {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-table-thead > tr > th {
      font-size: 10px;
    }
  }
`

// Communication specific styled components
const CommunicationScoreCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d9d9d9;
  }

  .ant-card-body {
    padding: 20px;
  }
`

// Updated styled components for the Reading Analysis section
const TranscriptionContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`

const TranscriptionHeader = styled.div`
  margin: -24px -24px 24px -24px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ErrorLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #fafafa;
  border-radius: 6px;
  font-size: 12px;
`

const LegendTitle = styled.span`
  font-weight: 600;
  color: #595959;
  margin-right: 8px;
  flex: 0 0 100%;
  margin-bottom: 4px;
`

const LegendItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
`

const TranscriptionText = styled.div`
  line-height: 1.8;
  font-size: 14px;
  color: #262626;
  background: #ffffff;
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  white-space: pre-wrap;
`

// Updated error highlighting styles
const ErrorReplace = styled.span`
  background: #fff1f0;
  color: #cf1322;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  border-bottom: 1px dashed #ff4d4f;

  &:hover {
    ${props =>
      props.clickable &&
      `
      background: #ffccc7;
      transform: translateY(-1px);
    `}
  }
`

const ErrorDelete = styled.span`
  background: #e6f7ff;
  color: #096dd9;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  text-decoration: line-through;
`

const ErrorInsert = styled.span`
  background: #f6ffed;
  color: #389e0d;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  border-bottom: 1px dashed #52c41a;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin: 20px 8px;

  .loading-text {
    margin-top: 16px;
    color: #8c8c8c;
    font-size: 14px;
    font-weight: 500;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #fff2f0;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  margin: 20px 8px;

  .error-title {
    font-size: 16px;
    font-weight: 600;
    color: #cf1322;
    margin-bottom: 8px;
  }

  .error-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 16px;
    line-height: 1.4;
  }
`

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin: 20px 8px;

  .empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #595959;
    margin-bottom: 8px;
  }

  .empty-description {
    font-size: 14px;
    color: #8c8c8c;
    line-height: 1.4;
  }
`
// ### Writing Section Styled Components ###
const WritingTaskCard = styled(CommunicationScoreCard)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 0;
  }
`;

const WritingTaskHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafafa;
`;

const WritingTaskContent = styled.div`
  padding: 24px;
`;

const SentenceCard = styled.div`
  background: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SentenceText = styled(Paragraph)`
  color: #595959;
  margin-bottom: 8px !important;
  line-height: 1.6;
`;

const UserAnswerText = styled.span`
  font-weight: 500;
  color: #d4380d;
  background: #fff2e8;
  padding: 2px 6px;
  border-radius: 4px;
`;

const CorrectAnswerText = styled.span`
  font-weight: 500;
  color: #389e0d;
  background: #f6ffed;
  padding: 2px 6px;
  border-radius: 4px;
`;


const ScoreDrawer = ({
  visible,
  onClose,
  assessment,
  reports,
  loading,
  error
}) => {
  const dispatch = useDispatch()
  const [aptitudeScore, setAptitudeScore] = useState(null)
  const [aptitudeLoading, setAptitudeLoading] = useState(false)
  const [aptitudeError, setAptitudeError] = useState(null)

  // Enhanced error handling functions
  const showErrorMessage = (error, context = '') => {
    const errorMessage =
      error?.message || error?.toString() || 'An unexpected error occurred'
    message.error(`${context ? context + ': ' : ''}${errorMessage}`)
    console.error(`Error ${context}:`, error)
  }

  const showSuccessMessage = text => {
    message.success(text)
  }

  const showLoadingMessage = text => {
    return message.loading(text, 0)
  }

  // Fetch aptitude score when drawer opens and assessment is aptitude type
  useEffect(() => {
    if (visible && assessment && assessment.type === 'Aptitude') {
      const assessmentAssignedId =
        assessment.assessment_assigned_id || assessment.id
      if (assessmentAssignedId) {
        fetchAptitudeScoreData(assessmentAssignedId)
      }
    }
  }, [visible, assessment])

  const fetchAptitudeScoreData = async assessmentAssignedId => {
    setAptitudeLoading(true)
    setAptitudeError(null)
    setAptitudeScore(null)

    const loadingMsg = showLoadingMessage(
      'Loading aptitude assessment results...'
    )

    try {
      console.log('🔍 Fetching aptitude score for:', assessmentAssignedId)
      const result = await dispatch(fetchAptitudeScore(assessmentAssignedId))
      console.log('✅ Aptitude score fetched:', result)
      setAptitudeScore(result)
      showSuccessMessage('Aptitude results loaded successfully!')
    } catch (error) {
      console.error('❌ Failed to fetch aptitude score:', error)
      const errorMsg = error.message || 'Failed to load aptitude score'
      setAptitudeError(errorMsg)
      showErrorMessage(error, 'Failed to load aptitude results')
    } finally {
      loadingMsg()
      setAptitudeLoading(false)
    }
  }

  // Text-to-speech function
  const speakWord = (word, errorType) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()

      const utterance = new SpeechSynthesisUtterance(word)

      utterance.rate = 0.8
      utterance.pitch = 1
      utterance.volume = 1

      const voices = window.speechSynthesis.getVoices()
      const englishVoice =
        voices.find(
          voice =>
            voice.lang.startsWith('en') &&
            (voice.name.includes('Google') || voice.name.includes('Microsoft'))
        ) || voices.find(voice => voice.lang.startsWith('en'))

      if (englishVoice) {
        utterance.voice = englishVoice
      }

      utterance.onstart = () => {
        showSuccessMessage(`🎧 Pronouncing: "${word}"`)
      }

      utterance.onerror = event => {
        // showErrorMessage(event, 'Speech synthesis failed')
        // console.error('Speech synthesis error:', event)
      }

      window.speechSynthesis.speak(utterance)
    } else {
      message.warning('Text-to-speech is not supported in this browser.')
    }
  }

  const getScoreColor = score => {
    if (score >= 70) return '#52c41a' // Green
    if (score >= 50) return '#1890ff' // Blue
    return '#ff4d4f' // Red
  }

  const getScoreLevel = score => {
    if (score >= 70) return 'Good'
    if (score >= 50) return 'Average'
    return 'Needs Work'
  }

  const findAssessmentReport = () => {
    if (!reports || !Array.isArray(reports) || !assessment) {
      return null
    }
    const assessmentId = assessment.assessment_assigned_id || assessment.id
    const matchedReport = reports.find(report => report.id === assessmentId)
    return matchedReport
  }
  const parseVideoTranscript = transcript => {
    if (!transcript) return []

    // Example filler words list
    const fillerWords = [
      'um',
      'uh',
    ]
    // Split by spaces, keep || as separate token
    const tokens = transcript.split(/(\s+|\|\|)/)

    return tokens.map((token, idx) => {
      if (token === '||') {
        // Pause indicator
        return (
          <span
            key={idx}
            style={{ color: '#8c8c8c', fontWeight: 500, margin: '0 2px' }}
          >
            ||{/* Pause */}
          </span>
        )
      }
      // Check for filler word (case-insensitive, remove punctuation)
      const cleanToken = token.replace(/[.,!?]/g, '').toLowerCase()
      if (fillerWords.includes(cleanToken.trim())) {
        return (
          <FillerWord key={idx} title="Filler word">
            {token}
          </FillerWord>
        )
      }
      return <span key={idx}>{token}</span>
    })
  }

  // Error guide for video transcript
  const renderVideoTranscriptErrorGuide = () => (
    <div
      style={{
        background: '#fafafa',
        borderRadius: '6px',
        padding: '10px 16px',
        marginBottom: '12px',
        fontSize: '12px',
        color: '#595959',
        border: '1px solid #e8e8e8',
      }}
    >
      <strong>Error Guide:</strong>
      <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
        <li>
          <span style={{ color: '#8c8c8c', fontWeight: 500 }}>||</span> ={' '}
          <span style={{ fontWeight: 500 }}>Pause</span>
        </li>
        <li>
          <FillerWord>Filler</FillerWord>

        </li>
      </ul>
     
    </div>
  )
  // Utility functions for aptitude results
  const formatTimeDetailed = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const getAverageTimePerQuestion = category => {
    const totalQuestions = category.topics.reduce(
      (sum, topic) => sum + (topic.right + topic.wrong + topic.unattempted),
      0
    )
    if (totalQuestions === 0) return 0
    return Math.round(category.time_spent_seconds / totalQuestions)
  }

  const formatPercentageCell = (value, percentage) => (
    <span
      style={{
        fontWeight: '600',
        color:
          percentage >= 80
            ? '#10b981'
            : percentage >= 60
            ? '#f59e0b'
            : percentage >= 40
            ? '#ef4444'
            : '#6b7280',
      }}
    >
      {percentage}%
    </span>
  )

  const formatScoreCell = (gained, total) => (
    <span style={{ fontWeight: '600', color: '#262626' }}>
      {gained} / {total}
    </span>
  )

  // Communication assessment rendering functions
  const renderScoreCard = (title, score, subtitle = null) => (
    <CommunicationScoreCard>
      <div style={{ textAlign: 'center' }}>
        <Text
          style={{
            fontSize: '14px',
            color: '#595959',
            fontWeight: '500',
            display: 'block',
            marginBottom: '4px',
          }}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              display: 'block',
              marginBottom: '16px',
            }}
          >
            {subtitle}
          </Text>
        )}
        <div style={{ marginBottom: '12px' }}>
          <Text
            style={{
              fontSize: '28px',
              fontWeight: '600',
              color: getScoreColor(score),
            }}
          >
            {score || 0}%
          </Text>
        </div>
        <Progress
          percent={score || 0}
          strokeColor={getScoreColor(score)}
          trailColor="#f5f5f5"
          showInfo={false}
          strokeWidth={6}
        />
        <Text
          style={{
            fontSize: '12px',
            color: getScoreColor(score),
            fontWeight: '500',
            display: 'block',
            marginTop: '8px',
          }}
        >
          {getScoreLevel(score || 0)}
        </Text>
      </div>
    </CommunicationScoreCard>
  )

  // Common state rendering functions
  const renderLoadingState = () => (
    <LoadingContainer>
      <Spin size="large" />
      <span className="loading-text">Loading assessment report...</span>
    </LoadingContainer>
  )

  const renderErrorState = (errorMessage, onRetry = null) => (
    <ErrorContainer>
      <div className="error-title">Failed to load assessment report</div>
      <div className="error-description">{errorMessage}</div>
      {onRetry && (
        <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
          Retry
        </Button>
      )}
    </ErrorContainer>
  )

  const renderEmptyState = () => (
    <EmptyContainer>
      <div className="empty-title">Report not available yet</div>
      <div className="empty-description">
        Assessment reports will be generated shortly after completion.
      </div>
    </EmptyContainer>
  )

  // Parse transcription with errors function
  const parseTranscriptionWithErrors = text => {
    if (!text) return []

    const errorPattern = /\{([^}]+)\}/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = errorPattern.exec(text)) !== null) {
      const beforeError = text.substring(lastIndex, match.index)

      const words = beforeError.trim().split(/\s+/)
      const lastWord = words.pop() || ''
      const textBeforeLastWord = words.join(' ')

      if (textBeforeLastWord) {
        parts.push({
          type: 'normal',
          text: textBeforeLastWord + ' ',
        })
      }

      if (lastWord) {
        const errorType = match[1]
        let cssType = 'replace'

        if (errorType === 'Mispronunciation') {
          cssType = 'replace'
        } else if (errorType === 'Omission') {
          cssType = 'delete'
        } else if (errorType === 'Insertion') {
          cssType = 'insert'
        }

        parts.push({
          type: 'error',
          text: lastWord,
          errorType: errorType,
          cssType: cssType,
          clickable: errorType === 'Mispronunciation',
        })
      }

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < text.length) {
      parts.push({
        type: 'normal',
        text: text.substring(lastIndex),
      })
    }

    return parts
  }

  // Render error legend
  const renderErrorLegend = () => (
    <ErrorLegend>
      <LegendTitle> Error Guide:</LegendTitle>
      <LegendItems>
        <LegendItem>
          <ErrorReplace style={{ padding: '2px 6px', fontSize: '10px' }}>
            Replace
          </ErrorReplace>
          <span style={{ fontWeight: '500' }}>Mispronunciation</span>
          <AudioOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
        </LegendItem>
        <LegendItem>
          <ErrorDelete style={{ padding: '2px 6px', fontSize: '10px' }}>
            Delete
          </ErrorDelete>
          <span style={{ fontWeight: '500' }}>Missed</span>
        </LegendItem>
        <LegendItem>
          <ErrorInsert style={{ padding: '2px 6px', fontSize: '10px' }}>
            Insert
          </ErrorInsert>
          <span style={{ fontWeight: '500' }}>Extra</span>
        </LegendItem>
      </LegendItems>
    </ErrorLegend>
  )

  // Render aptitude results
  const renderAptitudeResults = () => {
    if (aptitudeLoading) {
      return renderLoadingState()
    }

    if (aptitudeError) {
      return renderErrorState(aptitudeError, () =>
        fetchAptitudeScoreData(
          assessment.assessment_assigned_id || assessment.id
        )
      )
    }

    if (!aptitudeScore) {
      return renderEmptyState()
    }

    // Calculate percentages and metrics
    const overallPercentage = Math.round(
      (aptitudeScore.gainedScore / aptitudeScore.totalScore) * 100
    )
    const accuracy = aptitudeScore.statistics?.summary
      ? Math.round(
          (aptitudeScore.statistics.summary.total_right /
            aptitudeScore.statistics.summary.total_questions) *
            100
        )
      : 0

    // Prepare data for subject analysis table
    const subjectColumns = [
      {
        title: 'Subject',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        width: '25%',
      },
      {
        title: 'Score',
        key: 'score',
        width: '15%',
        render: (_, record) =>
          formatScoreCell(record.gained_marks, record.total_marks),
      },
      {
        title: 'Percentage',
        key: 'percentage',
        width: '15%',
        render: (_, record) => {
          const percentage =
            record.total_marks > 0
              ? Math.round((record.gained_marks / record.total_marks) * 100)
              : 0
          return formatPercentageCell(record.gained_marks, percentage)
        },
      },
      {
        title: 'Attempted',
        key: 'attempted',
        width: '15%',
        render: (_, record) => {
          const totalQuestions = record.topics.reduce(
            (sum, topic) =>
              sum + (topic.right + topic.wrong + topic.unattempted),
            0
          )
          return totalQuestions
        },
      },
      {
        title: 'Correct',
        key: 'correct',
        width: '15%',
        render: (_, record) => {
          const totalCorrect = record.topics.reduce(
            (sum, topic) => sum + topic.right,
            0
          )
          return totalCorrect
        },
      },
      {
        title: 'Avg Time',
        key: 'avgTime',
        width: '15%',
        render: (_, record) => `${getAverageTimePerQuestion(record)}s`,
      },
    ]

    const subjectData = aptitudeScore.statistics.categories.map(
      (category, index) => ({
        key: index,
        ...category,
      })
    )

    return (
      <DrawerContainer>
        <ContentSection>
          {/* Header Section */}
          <div
            style={{
              marginBottom: '24px',
              textAlign: 'center',
              padding: '0 8px',
            }}
          >
            <Text
              style={{
                color: '#8c8c8c',
                fontSize: '12px',
                display: 'block',
                marginTop: '4px',
              }}
            >
              Completed on{' '}
              {moment(assessment.submittedAt).format(
                'MMM DD, YYYY [at] h:mm A'
              )}
            </Text>
          </div>

          {/* Overall Score Cards */}
          <OverallScoreGrid>
            <ScoreCard>
              <ScoreValue>{overallPercentage}%</ScoreValue>
              <ScoreLabel>Overall Score</ScoreLabel>
              <ScoreSubtext>
                {aptitudeScore.gainedScore}/{aptitudeScore.totalScore}
              </ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>
                {aptitudeScore.statistics.summary.total_attempted}
              </ScoreValue>
              <ScoreLabel>Questions Attempted</ScoreLabel>
              <ScoreSubtext>
                out of {aptitudeScore.statistics.summary.total_questions}
              </ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>
                {aptitudeScore.statistics.summary.total_right}
              </ScoreValue>
              <ScoreLabel>Correct Answers</ScoreLabel>
              <ScoreSubtext>{accuracy}% accuracy</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>
                {formatTimeDetailed(
                  aptitudeScore.statistics.summary.total_time_seconds
                )}
              </ScoreValue>
              <ScoreLabel>Time Efficiency</ScoreLabel>
              <ScoreSubtext>
                {aptitudeScore.statistics.summary.average_speed_seconds_per_question.toFixed(
                  1
                )}
                s avg per question
              </ScoreSubtext>
            </ScoreCard>
          </OverallScoreGrid>

          {/* Summary Stats */}
          <SummaryGrid>
            <SummaryCard>
              <SummaryValue>
                {aptitudeScore.statistics.summary.total_questions}
              </SummaryValue>
              <SummaryLabel>Total Questions</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {aptitudeScore.statistics.summary.total_right}
              </SummaryValue>
              <SummaryLabel>Correct</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {aptitudeScore.statistics.summary.total_wrong}
              </SummaryValue>
              <SummaryLabel>Incorrect</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {aptitudeScore.statistics.summary.total_unattempted}
              </SummaryValue>
              <SummaryLabel>Unattempted</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {formatTimeDetailed(
                  aptitudeScore.statistics.summary.total_time_seconds
                )}
              </SummaryValue>
              <SummaryLabel>Total Time</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          {/* Performance by Difficulty Level */}
          <div style={{ marginBottom: '24px' }}>
            <SectionTitle>Performance by Difficulty Level</SectionTitle>
            <DifficultyGrid>
              <DifficultyCard borderColor="#bbf7d0" bgColor="#f0fdf4">
                <DifficultyLabel>Easy Questions</DifficultyLabel>
                <DifficultyScore color="#16a34a">
                  {aptitudeScore.statistics.summary.easy_gained} /{' '}
                  {aptitudeScore.statistics.summary.easy_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore.statistics.summary.easy_total > 0
                    ? Math.round(
                        (aptitudeScore.statistics.summary.easy_gained /
                          aptitudeScore.statistics.summary.easy_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fed7aa" bgColor="#fffbeb">
                <DifficultyLabel>Medium Questions</DifficultyLabel>
                <DifficultyScore color="#ea580c">
                  {aptitudeScore.statistics.summary.medium_gained} /{' '}
                  {aptitudeScore.statistics.summary.medium_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore.statistics.summary.medium_total > 0
                    ? Math.round(
                        (aptitudeScore.statistics.summary.medium_gained /
                          aptitudeScore.statistics.summary.medium_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fecaca" bgColor="#fef2f2">
                <DifficultyLabel>Hard Questions</DifficultyLabel>
                <DifficultyScore color="#dc2626">
                  {aptitudeScore.statistics.summary.hard_gained} /{' '}
                  {aptitudeScore.statistics.summary.hard_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore.statistics.summary.hard_total > 0
                    ? Math.round(
                        (aptitudeScore.statistics.summary.hard_gained /
                          aptitudeScore.statistics.summary.hard_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
            </DifficultyGrid>
          </div>

          {/* Detailed Subject Analysis Table */}
          <div style={{ marginBottom: '24px' }}>
            <SectionTitle>Detailed Subject Analysis</SectionTitle>
            <ResponsiveTableContainer>
              <Table
                columns={subjectColumns}
                dataSource={subjectData}
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
                bordered={false}
              />
            </ResponsiveTableContainer>
          </div>

          {/* Subject-wise Topic Breakdown Tables */}
          {aptitudeScore.statistics.categories.map(
            (category, categoryIndex) => {
              const topicColumns = [
                {
                  title: 'Topic',
                  dataIndex: 'name',
                  key: 'name',
                  ellipsis: true,
                  width: '30%',
                },
                {
                  title: 'Score',
                  key: 'score',
                  width: '12%',
                  render: (_, record) =>
                    formatScoreCell(record.gained_marks, record.total_marks),
                },
                {
                  title: '%',
                  key: 'percentage',
                  width: '10%',
                  render: (_, record) => {
                    const percentage =
                      record.total_marks > 0
                        ? Math.round(
                            (record.gained_marks / record.total_marks) * 100
                          )
                        : 0
                    return formatPercentageCell(record.gained_marks, percentage)
                  },
                },
                {
                  title: 'Attempted',
                  key: 'attempted',
                  width: '12%',
                  render: (_, record) => record.right + record.wrong,
                },
                {
                  title: 'Correct',
                  dataIndex: 'right',
                  key: 'right',
                  width: '12%',
                },
                {
                  title: 'Wrong',
                  dataIndex: 'wrong',
                  key: 'wrong',
                  width: '12%',
                },
                {
                  title: 'Skipped',
                  dataIndex: 'unattempted',
                  key: 'unattempted',
                  width: '12%',
                },
              ]

              const topicData = category.topics.map((topic, index) => ({
                key: `${categoryIndex}-${index}`,
                ...topic,
              }))

              return (
                <div key={categoryIndex} style={{ marginBottom: '24px' }}>
                  <SectionTitle>{category.name} - Topic Breakdown</SectionTitle>
                  <ResponsiveTableContainer>
                    <Table
                      columns={topicColumns}
                      dataSource={topicData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 700 }}
                      bordered={false}
                    />
                  </ResponsiveTableContainer>
                </div>
              )
            }
          )}
        </ContentSection>
      </DrawerContainer>
    )
  }

  // Communication assessment rendering functions
  const renderDetailedReadingScores = readingMetadata => {
    if (!readingMetadata) return null
    const assessmentReport = findAssessmentReport()

    const {
      accuracy_score = 0,
      completeness_score = 0,
      coverage_percentage = 0,
      fluency_score = 0,
      pronunciation_score = 0,
      prosody_score = 0,
      
      total_score = 0,
      error_counts = {},
      transcription = '',
    } = readingMetadata

    const { readingScore} = assessmentReport
    
    return (
      <ContentSection>
        <SectionTitle> Reading Analysis</SectionTitle>

        {/* Overall Reading Score */}
        <CommunicationScoreCard
        style={{
          marginBottom: '24px',
          border: '2px solid #1890ff',
        }}
      >
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <Text
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#595959',
              display: 'block',
              marginBottom: '8px',
            }}
          >
            Overall Reading Score
          </Text>
          <Text
            style={{
              fontSize: '36px',
              fontWeight: '600',
              color: getScoreColor(readingScore),
              display: 'block',
            }}
          >
            {readingScore === undefined || readingScore === null ? '0' : readingScore.toFixed(1)}%
          </Text>
          <Text
            style={{
              fontSize: '14px',
              color: getScoreColor(readingScore),
              fontWeight: '500',
            }}
          >
            {getScoreLevel(readingScore)}
          </Text>
        </div>
      </CommunicationScoreCard>

        {/* Detailed Scores Grid */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {[
            { label: 'Accuracy', score: accuracy_score },
            { label: 'Completeness', score: completeness_score },
            { label: 'Fluency', score: fluency_score },
            { label: 'Pronunciation', score: pronunciation_score },
            { label: 'Prosody', score: prosody_score },
            { label: 'Coverage', score: coverage_percentage },
          ].map((item, index) => (
            <Col xs={12} sm={8} key={index}>
              <CommunicationScoreCard style={{ height: '100%' }}>
                <div style={{ textAlign: 'center', padding: '4px' }}>
                  <Text
                    style={{
                      fontSize: '12px',
                      color: '#8c8c8c',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.label}
                  </Text>
                  <Text
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: getScoreColor(item.score),
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.score === undefined || item.score === null ? '0' : item.score.toFixed(1)}%
                  </Text>
                  <Progress
                    percent={item.score}
                    strokeColor={getScoreColor(item.score)}
                    trailColor="#f5f5f5"
                    size="small"
                    showInfo={false}
                    strokeWidth={4}
                  />
                </div>
              </CommunicationScoreCard>
            </Col>
          ))}
        </Row>

        {/* Error Analysis */}
        {error_counts && Object.keys(error_counts).length > 0 && (
          <CommunicationScoreCard
            style={{
              marginBottom: '24px',
              border: '1px solid #ff4d4f',
            }}
          >
            <SectionTitle style={{ color: '#ff4d4f', marginBottom: '16px' }}>
              Error Analysis
            </SectionTitle>
            <Row gutter={[16, 16]}>
              {[
                {
                  label: 'Pronunciation',
                  count: error_counts.pronunciation_errors || 0,
                  color: '#ff4d4f',
                },
                {
                  label: 'Missed',
                  count: error_counts.omission_errors || 0,
                  color: '#1890ff',
                },
                {
                  label: 'Extra',
                  count: error_counts.insertion_errors || 0,
                  color: '#52c41a',
                },
              ].map((error, index) => (
                <Col span={8} key={index}>
                  <div style={{ textAlign: 'center' }}>
                    <Text
                      style={{
                        fontSize: '24px',
                        color: error.color,
                        fontWeight: '600',
                        display: 'block',
                      }}
                    >
                      {error.count}
                    </Text>
                    <Text
                      style={{
                        fontSize: '12px',
                        color: '#8c8c8c',
                      }}
                    >
                      {error.label} Errors
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </CommunicationScoreCard>
        )}

        {/* Transcription */}
        {transcription && (
          <TranscriptionContainer>
            <TranscriptionHeader>
              Reading Transcription Analysis
              <span
                style={{
                  fontSize: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontWeight: '500',
                }}
              ></span>
            </TranscriptionHeader>

            {/* Enhanced Error Legend */}
            {renderErrorLegend()}

            <TranscriptionText>
              {parseTranscriptionWithErrors(transcription).map(
                (part, index) => {
                  if (part.type === 'error') {
                    const isClickable = part.clickable

                    const ErrorComponent =
                      part.cssType === 'replace'
                        ? ErrorReplace
                        : part.cssType === 'delete'
                        ? ErrorDelete
                        : ErrorInsert

                    return (
                      <ErrorComponent
                        key={index}
                        clickable={isClickable}
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          position: 'relative',
                        }}
                        title={
                          isClickable
                            ? `${part.errorType}: "${part.text}" - Click to hear correct pronunciation 🎧`
                            : `${part.errorType}: "${part.text}"`
                        }
                        onClick={
                          isClickable
                            ? () => speakWord(part.text, part.errorType)
                            : undefined
                        }
                      >
                        {part.text}
                        {isClickable && (
                          <AudioOutlined
                            style={{
                              fontSize: '11px',
                              color: '#1890ff',
                              marginLeft: '4px',
                              opacity: 0.8,
                            }}
                          />
                        )}
                      </ErrorComponent>
                    )
                  }

                  return <span key={index}>{part.text}</span>
                }
              )}
            </TranscriptionText>

            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: 'rgba(24, 144, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(24, 144, 255, 0.1)',
                fontSize: '11px',
                color: '#1890ff',
                textAlign: 'center',
                fontWeight: '500',
              }}
            >
              💡 Tip: Click on highlighted mispronounced words to hear the
              correct pronunciation
            </div>
          </TranscriptionContainer>
        )}
      </ContentSection>
    )
  }

  const renderVideoAnalysis = englishLangData => {
    if (!englishLangData || !englishLangData.detailed_scores) return null

    const videoScore = englishLangData.detailed_scores[0]
    if (!videoScore) return null

    const { overall_score = 0, detailed_analysis = {} } = videoScore
    const isNonEnglish = videoScore.isNonEnglish || false

    // Try to get corrected_response from both possible locations
    const corrected_response =
      detailed_analysis?.corrected_response ||
      detailed_analysis?.llm_evaluation?.corrected_response ||
      null

    const {
      cefr_level = 'N/A',
      fluency_score = 0,
      grammar_score = 0,
      accuracy_score = 0,
      prosody_score = 0,
      vocabulary_score = 0,
      coherence_score = 0,
      transcription = '',
    } = detailed_analysis

    const videoMetrics = isNonEnglish
      ? [
          { label: 'Fluency', score: fluency_score * 100 },
          { label: 'Grammar', score: grammar_score * 100 },
          { label: 'Vocabulary', score: vocabulary_score * 100 },
          { label: 'Coherence', score: coherence_score * 100 },
        ]
      : [
          { label: 'Fluency', score: fluency_score * 100 },
          { label: 'Grammar', score: grammar_score * 100 },
          { label: 'Vocabulary', score: vocabulary_score * 100 },
          { label: 'Pronunciation', score: accuracy_score * 100 },
        ]

    const getCEFRColor = level => {
      if (level === 'C1' || level === 'C2') return '#52c41a'
      if (level === 'B1' || level === 'B2') return '#1890ff'
      return '#ff4d4f'
    }

    return (
      <ContentSection>
        <SectionTitle> Video Response Analysis</SectionTitle>

        <CommunicationScoreCard
          style={{
            marginBottom: '24px',
            border: '2px solid #1890ff',
          }}
        >
          <Row justify="center">
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Overall Score
                </Text>
                <Text
                  style={{
                    fontSize: '28px',
                    fontWeight: '600',
                    color: getScoreColor(overall_score * 100),
                  }}
                >
                  { overall_score === undefined || overall_score === null ? '0' : (overall_score * 100).toFixed(1)}%
                </Text>
              </div>
            </Col>
          </Row>
        </CommunicationScoreCard>

        {/* Detailed Video Scores */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {videoMetrics.map((item, index) => (
            <Col xs={12} sm={6} key={index}>
              <CommunicationScoreCard style={{ height: '100%' }}>
                <div style={{ textAlign: 'center', padding: '4px' }}>
                  <Text
                    style={{
                      fontSize: '12px',
                      color: '#8c8c8c',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.label}
                  </Text>
                  <Text
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: getScoreColor(item.score),
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {  item.score === undefined || item.score === null ? '0' : item.score.toFixed(1)}%
                  </Text>
                  <Progress
                    percent={item.score}
                    strokeColor={getScoreColor(item.score)}
                    trailColor="#f5f5f5"
                    size="small"
                    showInfo={false}
                    strokeWidth={4}
                  />
                </div>
              </CommunicationScoreCard>
            </Col>
          ))}
        </Row>

        {/* Video Transcript Error Guide */}
        {transcription && renderVideoTranscriptErrorGuide()}

        {/* Video Transcription */}
        {transcription && (
          <CommunicationScoreCard>
            <SectionTitle style={{ color: '#1890ff', marginBottom: '12px' }}>
              Video Response Transcription
            </SectionTitle>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#595959',
              }}
            >
              {parseVideoTranscript(transcription)}
            </div>
          </CommunicationScoreCard>
        )}
      {corrected_response && (
        <CommunicationScoreCard>
          <SectionTitle style={{ color: '#389e0d', marginBottom: '12px' }}>
            Corrected Response
          </SectionTitle>
          <Paragraph
            style={{
              background: '#f6ffed',
              borderRadius: '6px',
              padding: '16px',
              fontSize: '15px',
              color: '#262626',
              border: '1px solid #b7eb8f',
              whiteSpace: 'pre-line',
            }}
          >
            {corrected_response}
          </Paragraph>
        </CommunicationScoreCard>
      )}
    </ContentSection>
  );
};

// ### START: NEW WRITING SECTION RENDER FUNCTIONS ###

const renderQuestionBasedResponse = qbrScore => {
  if (!qbrScore || !qbrScore.detailed_scores) return null;
  const details = qbrScore.detailed_scores[0];
  if (!details) return null;
  const score = qbrScore.score;
  const {
    overall_score,
    grammar_score,
    phrasing_score,
    coherence_score,
    vocabulary_score,
    spelling_punctuation_score,
  } = qbrScore;

  // const {
  //   user_response,
  //   corrected_response// <-- Extract corrected response
  // } = details.detailed_analysis ;

  const user_response = details.detailed_analysis?.user_response || "No response provided.";
  const corrected_response = details.detailed_analysis?.corrected_response || null;

  const metrics = [
    { label: 'Grammar', score: grammar_score, icon: <BookOutlined /> },
    { label: 'Phrasing', score: phrasing_score, icon: <BulbOutlined /> },
    { label: 'Coherence', score: coherence_score, icon: <CheckCircleOutlined /> },
    { label: 'Vocab.', score: vocabulary_score, icon: <FontSizeOutlined /> },
    { label: 'Spelling & Punctuation', score: spelling_punctuation_score, icon: <EditOutlined /> },
  ];

  return (
    <WritingTaskCard style={{ border: `2px solid ${getScoreColor(score)}` }}>
      <WritingTaskHeader>
        <SectionTitle style={{ margin: 0 }}>Question Based Response</SectionTitle>
        <Tag color={getScoreColor(score)} style={{ fontSize: 16, padding: '4px 12px' }}>
          {score === undefined || score === null ? '0' : score.toFixed(1)}%
        </Tag>
      </WritingTaskHeader>
      <WritingTaskContent>
        <Descriptions
          column={1}
          size="small"
          bordered
          style={{ marginBottom: 24, background: '#f6faff', borderRadius: 8 }}
        >
          <Descriptions.Item label="Question">
            <span style={{ color: '#595959', fontStyle: 'italic' }}>
              User was asked to write a short paragraph describing an image.
            </span>
          </Descriptions.Item>
        </Descriptions>
        <Row gutter={[32, 32]} style={{ marginBottom: '24px' }}>
          {metrics.map((item, index) => (
            <Col xs={12} sm={8} md={4} key={index}>
              <SummaryCard style={{ 
                background: '#f0f5ff', 
                border: '1px solid #d6e4ff',
                width: '100%' 
              }}>
                <div style={{ marginBottom: 4 }}>{item.icon}</div>
                <SummaryValue style={{ color: getScoreColor(item.score) }}>
                  {item.score === undefined || item.score === null ? '0' : item.score.toFixed(1)}%
                </SummaryValue>
                <SummaryLabel style={{ fontSize: item.label.length > 15 ? '10px' : undefined }}>
                  {item.label}
                </SummaryLabel>
              </SummaryCard>
            </Col>
          ))}
        </Row>
        <SectionTitle style={{ fontSize: '14px', marginBottom: '8px' }}>User's Response:</SectionTitle>
        <Paragraph
          blockquote
          style={{
            background: '#e6f7ff',
            borderRadius: '6px',
            padding: '16px',
            fontSize: '15px',
            color: '#262626',
            border: '1px solid #91d5ff',
            whiteSpace: 'pre-line',
          }}
        >
          {user_response || "No response provided."}
        </Paragraph>
        {/* --- Add this block for Corrected Response --- */}
        {corrected_response && (
          <>
            <SectionTitle style={{ fontSize: '14px', marginBottom: '8px', color: '#389e0d' }}>
              Corrected Response:
            </SectionTitle>
            <Paragraph
              style={{
                background: '#f6ffed',
                borderRadius: '6px',
                padding: '16px',
                fontSize: '15px',
                color: '#262626',
                border: '1px solid #b7eb8f',
                whiteSpace: 'pre-line',
              }}
            >
              {corrected_response}
            </Paragraph>
          </>
        )}
      </WritingTaskContent>
    </WritingTaskCard>
  );
};

// Improved Email Writing Score UI
const renderEmailWritingScore = (emailWritingScore) => {
  if (!emailWritingScore) return null;

  const {
    grammar_score = 0,
    overall_score = 0,
    phrasing_score = 0,
    voice_tone_score = 0,
    email_prompt = '',
    email_content = '',
    corrected_email, // <-- add this
  } = emailWritingScore;

  // Prefer top-level corrected_email, fallback to nested if needed
  const corrected =
    corrected_email ||
    emailWritingScore.detailed_scores?.[0]?.detailed_analysis?.corrected_email ||
    null;

  const metrics = [
    { label: 'Grammar', score: grammar_score, icon: <BookOutlined /> },
    { label: 'Phrasing', score: phrasing_score, icon: <BulbOutlined /> },
    { label: 'Tone', score: voice_tone_score, icon: <SoundOutlined /> },
  ];

  return (
    <WritingTaskCard style={{ border: `2px solid ${getScoreColor(overall_score)}` }}>
      <WritingTaskHeader>
        <SectionTitle style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          Email Writing
        </SectionTitle>
        <Tag color={getScoreColor(overall_score)} style={{ fontSize: 16, padding: '4px 12px' }}>
          {  (overall_score === undefined || overall_score === null ? '0' : (overall_score).toFixed(1))}%
        </Tag>
      </WritingTaskHeader>
      <WritingTaskContent>
        <Descriptions
          column={1}
          size="small"
          bordered
          style={{ marginBottom: 24, background: '#fff7e6', borderRadius: 8 }}
        >
          <Descriptions.Item label="Question">
            <span style={{ color: '#595959', fontStyle: 'italic' }}>
              {email_prompt}
            </span>
          </Descriptions.Item>
        </Descriptions>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {metrics.map((item, index) => (
            <Col xs={12} sm={8} md={4} key={index}>
              <SummaryCard style={{ background: '#fffbe6', border: '1px solid #ffe58f' }}>
                <div style={{ marginBottom: 4 }}>{item.icon}</div>
                <SummaryValue style={{ color: getScoreColor(item.score) }}>
                  {item.score === undefined || item.score === null ? '0' : (item.score).toFixed(1)}%
                </SummaryValue>
                <SummaryLabel style={{ fontSize: item.label.length > 15 ? '10px' : undefined }}>
                  {item.label}
                </SummaryLabel>        
              </SummaryCard>
            </Col>
          ))}
        </Row>
        <SectionTitle style={{ fontSize: '14px', marginBottom: '8px' }}>User's Email:</SectionTitle>
        <Paragraph
          blockquote
          style={{
            background: '#fffbe6',
            borderRadius: '6px',
            padding: '16px',
            fontSize: '15px',
            color: '#262626',
            border: '1px solid #ffe58f',
            whiteSpace: 'pre-line',
          }}
        >
          {email_content || "No email provided."}
        </Paragraph>
        {/* --- Add this block for Corrected Email --- */}
        {corrected_email && (
          <>
            <SectionTitle style={{ fontSize: '14px', marginBottom: '8px', color: '#389e0d' }}>
              Corrected Response:
            </SectionTitle>
            <Paragraph
              style={{
                background: '#f6ffed',
                borderRadius: '6px',
                padding: '16px',
                fontSize: '15px',
                color: '#262626',
                border: '1px solid #b7eb8f',
                whiteSpace: 'pre-line',
              }}
            >
              {corrected_email}
            </Paragraph>
          </>
        )}
      </WritingTaskContent>
    </WritingTaskCard>
  );
}

const renderDictationScore = dictationScore => {
  if (!dictationScore || !dictationScore.individual_scores || dictationScore.individual_scores.length === 0) return null;

  return (
    <WritingTaskCard>
      <WritingTaskHeader>
        <SectionTitle style={{ margin: 0 }}>Dictation</SectionTitle>
        <Tag color={getScoreColor(dictationScore.score)}>
          {(dictationScore.score === undefined || dictationScore.score === null ? '0' : (dictationScore.score).toFixed(1))}% Correct
        </Tag>
      </WritingTaskHeader>
      <WritingTaskContent>
        {dictationScore.individual_scores.map((item, index) => {
          // Check if user attempted this question
          const isNotAttempted = item.user_answer === "not attempted" || !item.user_answer;
          // Use accuracy_percentage for the perfect score check
          const isPerfect = !isNotAttempted && (item.accuracy_percentage === 100);
          
          // Determine colors based on attempt status and score
          let answerColor, answerBg;
          if (isNotAttempted) {
            answerColor = '#8c8c8c';
            answerBg = '#f5f5f5';
          } else if (isPerfect) {
            answerColor = '#389e0d';
            answerBg = '#f6ffed';
          } else {
            answerColor = '#d4380d';
            answerBg = '#fff2e8';
          }

          return (
            <SentenceCard key={item.sub_question_id || index}>
              <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
                <strong>{index + 1}. Reference Text:</strong>
              </Paragraph>
              <Paragraph style={{ marginBottom: 8 }}>
                <CorrectAnswerText>
                  {item.reference_sentence}
                </CorrectAnswerText>
              </Paragraph>
              <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
                Your Answer:
              </Paragraph>
              <span
                style={{
                  fontWeight: 500,
                  color: answerColor,
                  background: answerBg,
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
              >
                {isNotAttempted ? "Not attempted" : item.user_answer}
              </span>
              <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                Score: <span style={{ color: answerColor, fontWeight: 500 }}>
                  {item.accuracy_percentage || 0}%
                </span>
              </div>
            </SentenceCard>
          );
        })}
      </WritingTaskContent>
    </WritingTaskCard>
  );
};
const renderSentenceCompletion = scScore => {
  if (!scScore || !scScore.detailed_scores || scScore.detailed_scores.length === 0) return null;

  return (
    <WritingTaskCard>
      <WritingTaskHeader>
        <SectionTitle style={{ margin: 0 }}>Sentence Completion</SectionTitle>
        <Tag color={getScoreColor(scScore.score)}>{  scScore.score === undefined || scScore.score === null ? '0' : scScore.score.toFixed(1)}% Correct</Tag>
      </WritingTaskHeader>
      <WritingTaskContent>
        {scScore.detailed_scores.map((item, index) => {
          // Check if user answer is not attempted (empty, null, undefined, or "Not Attempted")
          const isNotAttempted = !item.user_answer || 
                                 item.user_answer.trim() === '' || 
                                 item.user_answer.toLowerCase() === 'not attempted' 
          
          // Use blank underscores if not attempted, otherwise use the actual answer
          const displayAnswer = isNotAttempted ? '_____' : item.user_answer;
          
          const answerColor = item.is_correct ? '#389e0d' : '#d4380d';
          const answerBg = item.is_correct ? '#f6ffed' : '#fff2e8';
          
          // For not attempted answers, use gray styling
          const finalAnswerColor = isNotAttempted ? '#8c8c8c' : answerColor;
          const finalAnswerBg = isNotAttempted ? '#f5f5f5' : answerBg;

          // Find any blank pattern (_, ___, ____, _____, etc)
          const blankPattern = /_+/g;
          
          // Create styled answer span
          const answerSpan = `<span style="color: ${finalAnswerColor}; background: ${finalAnswerBg}; padding: 2px 6px; border-radius: 4px; font-weight: 500; margin: 0 2px; display: inline-block;">${displayAnswer}</span>`;
          
          // Replace the blank with the styled answer
          const formattedSentence = item.sentence.replace(blankPattern, answerSpan);

          return (
            <SentenceCard key={item.sub_question_id || index}>
              <SectionTitle style={{ fontSize: '14px', marginTop: 0 }}>
                Question {index + 1}:
              </SectionTitle>
              <SentenceText>
                <span dangerouslySetInnerHTML={{ __html: formattedSentence }} />
              </SentenceText>
              
              {/* Fallback in case the replacement didn't work */}
              {!item.sentence.match(blankPattern) && (
                <div style={{ marginTop: 8 }}>
                  <strong>Your answer:</strong>{' '}
                  <span style={{
                    color: finalAnswerColor,
                    background: finalAnswerBg,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    {displayAnswer}
                  </span>
                  {isNotAttempted && (
                    <span style={{ color: '#8c8c8c', fontSize: '12px', marginLeft: '8px' }}>
                      (Not attempted)
                    </span>
                  )}
                </div>
              )}

              {/* Show expected answer always */}
              <div style={{ marginTop: 8 }}>
                <strong>Expected Answer:</strong>{' '}
                <span style={{
                  color: '#389e0d',
                  background: '#f6ffed',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontWeight: 500
                }}>
                  {item.correct_answer}
                </span>
              </div>
            </SentenceCard>
          );
        })}
      </WritingTaskContent>
    </WritingTaskCard>
  );
};

const renderSentenceBuild = sbScore => {
  if (!sbScore || !sbScore.detailed_scores || sbScore.detailed_scores.length === 0) return null;

  return (
    <WritingTaskCard>
      <WritingTaskHeader>
        <SectionTitle style={{ margin: 0 }}>Sentence Building</SectionTitle>
        <Tag color={getScoreColor(sbScore.score)}>{sbScore.score === undefined || sbScore.score === null ? '0' : sbScore.score.toFixed(1)}% Correct</Tag>
      </WritingTaskHeader>
      <WritingTaskContent>
        {sbScore.detailed_scores.map((item, index) => {
          const answerColor = item.is_correct ? '#389e0d' : '#d4380d';
          const answerBg = item.is_correct ? '#f6ffed' : '#fff2e8';

          // Handle different question types
if (item.question_type === 'sentence_rearrangement') {
  // Support both JSON array and plain string
  let questionArr = [];
  if (Array.isArray(item.question_text)) {
    questionArr = item.question_text;
  } else if (typeof item.question_text === 'string') {
    // Split by period, filter empty, and trim
    questionArr = item.question_text
      .split('.')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s + '.');
  }

  // User answer as array
  let userArr = [];
  if (Array.isArray(item.user_answer_raw)) {
    userArr = item.user_answer_raw.map(idx =>
      questionArr[idx] || ''
    );
  } else if (typeof item.user_answer_raw === 'string') {
    userArr = item.user_answer_raw
      .split('.')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s + '.');
  }

  // Correct answer as array
  let correctArr = [];
  if (Array.isArray(item.correct_answer_text)) {
    correctArr = item.correct_answer_text;
  } else if (typeof item.correct_answer_text === 'string') {
    correctArr = item.correct_answer_text
      .split('.')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s + '.');
  }

  return (
    <SentenceCard key={item.sub_question_id}>
      <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
        <strong>{index + 1}. Arrange the following sentences to form a meaningful paragraph.</strong>
      </Paragraph>
      <Paragraph style={{ marginBottom: 4 }}>
        <strong>Your Answer:</strong>
        <div style={{ background: answerBg, borderRadius: 6, padding: 8, marginTop: 4 }}>
          {userArr.map((sentence, idx) => (
            <div key={idx} style={{ color: answerColor, fontWeight: 500, marginBottom: 2 }}>
              {`${idx + 1}. ${sentence}`}
            </div>
          ))}
        </div>
      </Paragraph>
      <Paragraph style={{ marginBottom: 0 }}>
        <strong>Correct Answer:</strong>
        <div style={{ background: '#f6ffed', borderRadius: 6, padding: 8, marginTop: 4 }}>
          {correctArr.map((sentence, idx) => (
            <div key={idx} style={{ color: '#389e0d', fontWeight: 500, marginBottom: 2 }}>
              {`${idx + 1}. ${sentence}`}
            </div>
          ))}
        </div>
      </Paragraph>
    </SentenceCard>
  );

          } 
          else if (item.question_type === 'word_arrangement') {
            // Handle word arrangement type
            return (
              <SentenceCard key={item.sub_question_id}>
                <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
                  <strong>{index + 1}. Unscramble the following words to form a correct sentence.</strong>
                </Paragraph>
                <Paragraph style={{ marginBottom: 4 }}>
                  <strong>Your Answer:</strong>{' '}
                  <UserAnswerText style={{ background: answerBg, color: answerColor }}>
                    {item.user_answer_text || item.user_answer_raw}
                  </UserAnswerText>
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  <strong>Correct Answer:</strong>{' '}
                  <CorrectAnswerText>
                    {item.correct_answer_text || item.question_text}
                  </CorrectAnswerText>
                </Paragraph>
              </SentenceCard>
            );
          }

          // Fallback for unknown question types
          return (
            <SentenceCard key={item.sub_question_id}>
              <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
                <strong>{index + 1}. {item.question_type}</strong>
              </Paragraph>
              <Paragraph style={{ marginBottom: 4 }}>
                <strong>Your Answer:</strong>{' '}
                <UserAnswerText style={{ background: answerBg, color: answerColor }}>
                  {item.user_answer_text || JSON.stringify(item.user_answer_raw)}
                </UserAnswerText>
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <strong>Correct Answer:</strong>{' '}
                <CorrectAnswerText>
                  {item.correct_answer_text || item.question_text}
                </CorrectAnswerText>
              </Paragraph>
            </SentenceCard>
          );
        })}
      </WritingTaskContent>
    </WritingTaskCard>
  );
};

  const renderWritingAnalysis = (metadata) => {
    if (!metadata) return null;


  const { questionBasedResponseScore, sentenceCompletionScore, sentenceBuildScore, emailWritingScore, dictationScore } = metadata;

  const hasWritingData =
    questionBasedResponseScore ||
    sentenceCompletionScore ||
    sentenceBuildScore ||
    emailWritingScore ||
    dictationScore;

  if (!hasWritingData) return null;

    return (
        <ContentSection>
            <SectionTitle>Writing Analysis</SectionTitle>
            {questionBasedResponseScore && renderQuestionBasedResponse(questionBasedResponseScore)}
            {emailWritingScore && renderEmailWritingScore(emailWritingScore)}
            {dictationScore && renderDictationScore(dictationScore)}
            {sentenceCompletionScore && renderSentenceCompletion(sentenceCompletionScore)}
            {sentenceBuildScore && renderSentenceBuild(sentenceBuildScore)}
        </ContentSection>
    );
  };
  
  // ### END: NEW WRITING SECTION RENDER FUNCTIONS ###

const renderScoreContent = () => {
    // Handle aptitude assessments
    if (assessment?.type === 'Aptitude') {
      return renderAptitudeResults()
    }

    // Handle communication assessments
    if (loading) return renderLoadingState()
    if (error) return renderErrorState(error)

    const assessmentReport = findAssessmentReport()
    if (!assessmentReport) return renderEmptyState()

    if (assessmentReport.type?.toLowerCase() === 'communication') {
      // Correctly destructure all required scores directly from the report
      const {
        speakingScore = 0,
        readingScore = 0,
        listeningScore = 0,
        writingScore = 0,
        overallScore = 0,
        videoResponseScore,
        paragraphReadingScore,
        audioQuestionScore,
        questionBasedResponseScore,
        sentenceCompletionScore,
        sentenceBuildScore,
        emailWritingScore,
        dictationScore
      } = assessmentReport

      // Create a metadata object to pass to the writing analysis
      const writingMetadata = {
          questionBasedResponseScore,
          sentenceCompletionScore,
          sentenceBuildScore,
          emailWritingScore,
          dictationScore
      };

      return (
        <DrawerContainer>
          <ContentSection>
            {/* Header Section */}
            <div
              style={{
                marginBottom: '32px',
                textAlign: 'center',
                padding: '0 16px',
              }}
            >
              <Text
                style={{
                  color: '#8c8c8c',
                  fontSize: '14px',
                  display: 'block',
                  marginTop: '8px',
                }}
              >
                Completed on{' '}
                {moment(assessmentReport.timestamp).format(
                  'MMM DD, YYYY [at] h:mm A'
                )}
              </Text>
            </div>

            {/* Main Scores Section */}
            <CommunicationScoreCard
              style={{
                marginBottom: '24px',
                border: '2px solid #1890ff',
              }}
            >
              <Row justify="center">
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '8px',
                      }}
                    >
                      Overall Score
                    </Text>
                    <Text
                      style={{
                        fontSize: '28px',
                        fontWeight: '600',
                        color: getScoreColor(overallScore),
                      }}
                    >
                      {overallScore === undefined || overallScore === null ? '0' : overallScore.toFixed(1)}%
                    </Text>
                  </div>
                </Col>
              </Row>
            </CommunicationScoreCard>

            <Row
              gutter={[16, 16]}
              style={{ marginBottom: '32px', padding: '0 8px' }}
            >
              <Col xs={12} sm={6}>
                {renderScoreCard('Speaking Ability', speakingScore)}
              </Col>
              <Col xs={12} sm={6}>
                {renderScoreCard(
                  'Reading Ability',
                  Math.min(readingScore, 100)
                )}
              </Col>
              <Col xs={12} sm={6}>
                {renderScoreCard('Listening Ability', listeningScore)}
              </Col>
              <Col xs={12} sm={6}>
                {renderScoreCard('Writing Ability', writingScore)}
              </Col>
            </Row>

            <Divider style={{ margin: '32px 16px' }} />

            {/* Detailed Analysis Sections */}
            <div style={{ padding: '0 8px' }}>
              {/* Video Response Analysis (Speaking) */}
              {videoResponseScore && renderVideoAnalysis(videoResponseScore)}

              {/* Reading Analysis */}
{paragraphReadingScore?.reading &&
  renderDetailedReadingScores(
    paragraphReadingScore.reading,
    paragraphReadingScore.score // pass the top-level score here
  )}
              {/* Writing Analysis - Passing the constructed metadata object */}
              {renderWritingAnalysis(writingMetadata)}

              {/* Audio Comprehension (Listening) */}
              {audioQuestionScore && (
                <ContentSection>
                  <SectionTitle> Audio Comprehension Analysis</SectionTitle>
                  <CommunicationScoreCard>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Text
                        style={{
                          fontSize: '16px',
                          color: '#595959',
                          display: 'block',
                          marginBottom: '16px',
                        }}
                      >
                        Correct Answers:{' '}
                        {audioQuestionScore.correct_answers || 0}
                      </Text>
                      <Progress
                        percent={audioQuestionScore.score || 0}
                        strokeColor={getScoreColor(
                          audioQuestionScore.score || 0
                        )}
                        trailColor="#f5f5f5"
                        strokeWidth={8}
                      />
                    </div>
                  </CommunicationScoreCard>
                </ContentSection>
              )}
            </div>
          </ContentSection>
        </DrawerContainer>
      )
    }

    return renderEmptyState()
  }
  return (
    <Drawer
      title={null}
      placement="right"
      width={Math.min(700, window.innerWidth * 0.85)}
      onClose={onClose}
      open={visible}
      styles={{
        header: {
          display: 'none',
        },
        body: {
          padding: '16px 8px 24px 8px',
          backgroundColor: '#fafafa',
        },
      }}
    >
      <DrawerHeader>
        <div>
          <HeaderTitle>
            Assessment Report - {assessment?.type || 'Assessment'}
          </HeaderTitle>
          <HeaderSubtitle>
            {assessment?.title || 'Assessment Results'}
          </HeaderSubtitle>
        </div>
      </DrawerHeader>

      {renderScoreContent()}
    </Drawer>
  )
}

export default ScoreDrawer
